import type { ComponentProps } from 'react';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { Modal, Spinner } from '@belong/ui';
import { UnitListingDetailsModel } from 'src/api/models';
import { isTourRequestActive } from 'src/features/residents/utils';
import { useUser } from 'src/features/users/useUser';
import { useBookedTours, useGetResidentTourRequests } from '../../queries';
import { TourCancel } from './steps/tour-cancel';
import { SelfTourPreview } from './steps/self-tour-preview';
import { TourScheduled } from './steps/tour-scheduled';
import { TourSelection } from './steps/tour-selection';
import { TourSendAvailability } from './steps/tour-send-availability/tour-send-availability';

type Props = Pick<ComponentProps<typeof Modal>, 'isOpen' | 'onDismiss'> & {
  home: UnitListingDetailsModel;
  onTourRequestSent: () => void;
  onSignIn: (backTo?: string) => void;
};

export enum TourModalStep {
  TourSelection = 'tour-selection',
  TourScheduled = 'tour-scheduled',
  TourCancel = 'tour-cancel',
  TourSendAvailability = 'tour-send-availability',
}

export function TourModal({
  home,
  isOpen,
  onDismiss,
  onTourRequestSent,
  onSignIn,
}: Props) {
  const router = useRouter();
  const [currentStep, setCurrentStep] = useState<TourModalStep | null>(null);
  const { user } = useUser();
  const isSignedIn = !!user?.userId;

  const { bookedTours, isBookedToursLoading } = useBookedTours(
    { homeId: home.basicInfo?.unitId! },
    {
      enabled: isSignedIn,
    },
  );
  const activeTours = bookedTours?.filter(
    ({ tourAttendee, tour }) =>
      tour?.status === 'Scheduled' &&
      ['Booked', 'Invited'].includes(tourAttendee!.attendeeStatus),
  );
  const soonestActiveTours = [...(activeTours ?? [])].sort(
    (first, second) =>
      new Date(first.tour?.startOn ?? 0).getTime() -
      new Date(second.tour?.startOn ?? 0).getTime(),
  );
  const bookedTour =
    soonestActiveTours.find(({ tour }) => tour?.type === 'SelfTour') ??
    soonestActiveTours[0];

  const { data: tourRequests, isLoading: isTourRequestsLoading } =
    useGetResidentTourRequests({ enabled: isSignedIn });
  const hasActiveTourRequest = !!tourRequests?.some(
    (tourRequest) =>
      tourRequest.homeUniqueId === home.basicInfo?.unitId &&
      isTourRequestActive(tourRequest),
  );

  function onModalClose() {
    setCurrentStep(null);
    onDismiss();
  }

  if (!currentStep && !isBookedToursLoading && !isTourRequestsLoading) {
    // `!` throughout: the modal is only mounted from the house page, which
    // already dereferenced these unconditionally.
    if (
      !bookedTour ||
      bookedTour.isReserved ||
      bookedTour.tourAttendee?.attendeeStatus === 'Invited'
    ) {
      setCurrentStep(
        !bookedTour && hasActiveTourRequest
          ? TourModalStep.TourSendAvailability
          : TourModalStep.TourSelection,
      );
    } else {
      setCurrentStep(TourModalStep.TourScheduled);
    }
  }

  const commonProps = {
    home,
    bookedTour,
    onDismiss: onModalClose,
    setStep: setCurrentStep,
  };

  useEffect(() => {
    if (window) {
      // Read `step` straight off the router: this used to cast `router.query`
      // to the rent-estimate flow's `URLQueryParams`, which has no `step` key.
      const { step } = router.query;

      if (step === 'reschedule') {
        setCurrentStep(TourModalStep.TourSelection);
      }
    }
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      name="tour_modal"
      onDismiss={onModalClose}
      overlayClassName="bg-opacity-100"
      enableOverflow
    >
      {isSignedIn && isBookedToursLoading && <Spinner />}
      <div className="pt-2xl">
        {isSignedIn ? (
          <>
            {currentStep === TourModalStep.TourSelection && (
              <TourSelection {...commonProps} />
            )}
            {currentStep === TourModalStep.TourScheduled && (
              <TourScheduled {...commonProps} />
            )}
            {currentStep === TourModalStep.TourCancel && (
              <TourCancel {...commonProps} />
            )}
            {currentStep === TourModalStep.TourSendAvailability && (
              <TourSendAvailability
                {...commonProps}
                onTourRequestSent={onTourRequestSent}
              />
            )}
          </>
        ) : (
          <SelfTourPreview home={home} onSignIn={onSignIn} />
        )}
      </div>
    </Modal>
  );
}
